<template>
  <v-card class="pa-4">
    <div class="d-flex align-center justify-space-between mb-3">
      <div class="text-subtitle-1 font-weight-bold">Consum pe Perioada</div>
      <v-btn-toggle v-model="chartType" density="compact" mandatory>
        <v-btn size="small" value="data">Date</v-btn>
        <v-btn size="small" value="sms">SMS</v-btn>
        <v-btn size="small" value="voice">Voce</v-btn>
        <v-btn size="small" value="cost">Cost</v-btn>
      </v-btn-toggle>
    </div>

    <div v-if="summaries.length === 0" class="text-center text-grey pa-8">
      <v-icon size="48" color="grey-lighten-1">mdi-chart-bar</v-icon>
      <div class="mt-2">Nu exista date de trafic pentru perioada selectata</div>
    </div>

    <div v-else>
      <div class="chart-container" style="height: 300px; position: relative;">
        <canvas ref="chartCanvas"></canvas>
      </div>
    </div>
  </v-card>
</template>

<script>
export default {
  name: 'TrafficChart',
  props: {
    summaries: { type: Array, default: () => [] }
  },
  data() {
    return {
      chartType: 'data',
      chartInstance: null
    }
  },
  watch: {
    summaries: {
      handler() { this.renderChart() },
      deep: true
    },
    chartType() { this.renderChart() }
  },
  mounted() {
    this.renderChart()
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },
  methods: {
    formatDataValue(mb) {
      if (mb >= 1024) return (mb / 1024).toFixed(2)
      return mb.toFixed(2)
    },
    getDataUnit(rawValues) {
      const max = Math.max(...rawValues, 0)
      return max >= 1024 ? 'GB' : 'MB'
    },
    async renderChart() {
      if (!this.summaries.length) return

      // Dynamic import Chart.js
      try {
        const { Chart, registerables } = await import('chart.js')
        Chart.register(...registerables)

        if (this.chartInstance) {
          this.chartInstance.destroy()
        }

        const canvas = this.$refs.chartCanvas
        if (!canvas) return

        const labels = this.summaries.map(s => s.period_date)
        let data, label, color, unit, yTickCallback, tooltipCallback

        switch (this.chartType) {
          case 'data': {
            const rawMB = this.summaries.map(s => parseFloat(s.data_mb) || 0)
            unit = this.getDataUnit(rawMB)
            if (unit === 'GB') {
              data = rawMB.map(v => parseFloat((v / 1024).toFixed(2)))
            } else {
              data = rawMB.map(v => parseFloat(v.toFixed(2)))
            }
            label = `Date (${unit})`
            color = '#2196F3'
            yTickCallback = (v) => v.toFixed(1) + ' ' + unit
            tooltipCallback = (ctx) => ctx.parsed.y.toFixed(2) + ' ' + unit
            break
          }
          case 'sms':
            data = this.summaries.map(s => parseInt(s.sms_count) || 0)
            label = 'SMS (mesaje)'
            color = '#4CAF50'
            yTickCallback = (v) => Math.round(v) + ' msg'
            tooltipCallback = (ctx) => ctx.parsed.y + ' mesaje'
            break
          case 'voice':
            data = this.summaries.map(s => parseFloat(s.voice_minutes) || 0)
            label = 'Voce (minute)'
            color = '#FF9800'
            yTickCallback = (v) => v.toFixed(0) + ' min'
            tooltipCallback = (ctx) => ctx.parsed.y.toFixed(1) + ' min'
            break
          case 'cost':
            data = this.summaries.map(s => parseFloat(s.total_cost) || 0)
            label = 'Cost (EUR)'
            color = '#F44336'
            yTickCallback = (v) => v.toFixed(2) + ' €'
            tooltipCallback = (ctx) => ctx.parsed.y.toFixed(4) + ' EUR'
            break
        }

        this.chartInstance = new Chart(canvas, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label,
              data,
              backgroundColor: color + '80',
              borderColor: color,
              borderWidth: 1
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: 'top' },
              tooltip: {
                callbacks: {
                  label: tooltipCallback
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: yTickCallback
                }
              },
              x: {
                ticks: {
                  maxRotation: 45,
                  autoSkip: true,
                  maxTicksLimit: 15
                }
              }
            }
          }
        })
      } catch (e) {
        console.warn('Chart.js not available:', e.message)
      }
    }
  }
}
</script>
